import * as R from 'ramda';
import {
  EBuildingStatus,
  ECategoryType,
  ECoworkingOfferType,
  EDealType,
  EObjectType,
  EOfferType,
  EOfficeType,
  ERentTime,
  ERoomType,
  IJsonQuery,
  IJsonQueryType,
} from './types';
import { dealTypeFromJsonQuery, offerTypeFromJsonQuery, isSuburban, isCommercial } from '../category';
import { setTerm, setTerms, setRootType } from './helpers';
import { setDealType } from './setDealType';
import { setRoomTypes } from './setRoomTypes';

export function selectOfferType(jsonQuery: IJsonQuery): (nextOfferType: EOfferType) => IJsonQuery {
  let nextJsonQuery = R.clone(jsonQuery);

  return nextOfferType => {
    const offerType = offerTypeFromJsonQuery(jsonQuery);
    const dealType = dealTypeFromJsonQuery(jsonQuery);
    const newObject = jsonQuery.geo && jsonQuery.geo.value.find(geo => geo.type === 'newobject');

    if (!isCommercial(nextOfferType)) {
      if (isCommercial(offerType)) {
        nextJsonQuery = setTerm('office_type')(nextJsonQuery)(undefined);
        nextJsonQuery = setTerm('category')(nextJsonQuery)(undefined);

        if (dealType & EDealType.Rent) {
          nextJsonQuery = setTerm('for_day')(nextJsonQuery)(ERentTime.NotDaily);
        }
      }

      if (!(isSuburban(offerType) && isSuburban(nextOfferType))) {
        nextJsonQuery = setTerm('object_type')(nextJsonQuery)(undefined);
      }
      nextJsonQuery = setTerm('building_status')(nextJsonQuery)(undefined);

      if (!newObject || nextOfferType === EOfferType.FlatOld) {
        nextJsonQuery = setTerm('from_developer')(nextJsonQuery)(undefined);
      }
      nextJsonQuery = setTerm('with_newobject')(nextJsonQuery)(undefined);

      const offerTypeIsRoomType = (offerType & (EOfferType.FlatShared | EOfferType.Room | EOfferType.Bed)) !== 0;
      const nextOfferTypeIsRoomType = [EOfferType.FlatShared, EOfferType.Room, EOfferType.Bed].includes(nextOfferType);
      if ((offerTypeIsRoomType && !nextOfferTypeIsRoomType) || (!offerTypeIsRoomType && nextOfferTypeIsRoomType)) {
        nextJsonQuery = setTerm('room')(nextJsonQuery)(undefined);
      }

      if (nextJsonQuery.office_type) {
        nextJsonQuery = setTerm('office_type')(nextJsonQuery)(undefined);
      }

      switch (nextOfferType) {
        case EOfferType.Flat:
          nextJsonQuery = setOfferType(nextJsonQuery)('flat');
          break;
        case EOfferType.FlatOld:
          nextJsonQuery = setOfferType(nextJsonQuery)('flat');
          if ((dealType & EDealType.Rent) === 0) {
            nextJsonQuery = setTerm('building_status')(nextJsonQuery)(EBuildingStatus.Old);
          }
          break;
        case EOfferType.FlatNew:
          nextJsonQuery = setOfferType(nextJsonQuery)('flat');
          nextJsonQuery = setTerm('building_status')(nextJsonQuery)(EBuildingStatus.New);
          break;
        case EOfferType.FlatShared:
          nextJsonQuery = setOfferType(nextJsonQuery)('flat');
          nextJsonQuery = selectRoomType(nextJsonQuery)(ERoomType.FlatShared);
          break;
        case EOfferType.Room:
          nextJsonQuery = setOfferType(nextJsonQuery)('flat');
          nextJsonQuery = selectRoomType(nextJsonQuery)(ERoomType.Room);
          break;
        case EOfferType.Bed:
          nextJsonQuery = setOfferType(nextJsonQuery)('flat');
          nextJsonQuery = selectRoomType(nextJsonQuery)(ERoomType.Bed);
          break;
        case EOfferType.House:
          nextJsonQuery = setOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = selectSuburbanObjectType(nextJsonQuery)(EObjectType.House);
          break;
        case EOfferType.Townhouse:
          nextJsonQuery = setOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = selectSuburbanObjectType(nextJsonQuery)(EObjectType.Townhouse);
          break;
        case EOfferType.HousePart:
          nextJsonQuery = setOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = selectSuburbanObjectType(nextJsonQuery)(EObjectType.Housepart);
          break;
        case EOfferType.Land:
          nextJsonQuery = setDealType(nextJsonQuery)(EDealType.Sale);
          nextJsonQuery = setOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = selectSuburbanObjectType(nextJsonQuery)(EObjectType.Land);
          break;
        case EOfferType.Garage:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(nextJsonQuery)([EOfficeType.Garage]);
          break;
        default:
      }
    } else {
      if (dealType & EDealType.Rent) {
        nextJsonQuery = setTerm('for_day')(nextJsonQuery)(ERentTime.NotDaily);
      }

      nextJsonQuery = setTerm('category')(nextJsonQuery)(undefined);
      nextJsonQuery = setTerm('object_type')(nextJsonQuery)(undefined);
      nextJsonQuery = setTerm('room')(nextJsonQuery)(undefined);
      nextJsonQuery = setTerm('building_status')(nextJsonQuery)(undefined);

      if (nextOfferType !== EOfferType.Coworking && jsonQuery.office_type?.value.includes(EOfficeType.Coworking)) {
        nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.Coworking);
      }

      if (
        (nextOfferType &
          (EOfferType.Office | EOfferType.Warehouse | EOfferType.TradeArea | EOfferType.FreeAppointmentObject)) ===
        0
      ) {
        nextJsonQuery = setTerm('from_offrep')(nextJsonQuery)(undefined);
      }
      if (
        nextJsonQuery.office_type &&
        nextJsonQuery.office_type.value.includes(EOfficeType.Garage) &&
        nextOfferType !== EOfferType.Garage
      ) {
        nextJsonQuery = setTerm('office_type')(nextJsonQuery)(undefined);
      }

      switch (nextOfferType) {
        case EOfferType.Office:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.Office);
          break;

        case EOfferType.Coworking: {
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(nextJsonQuery)([EOfficeType.Coworking]);
          nextJsonQuery = setTerms('coworking_offer_type')(nextJsonQuery)([ECoworkingOfferType.FlexibleOffice]);
          nextJsonQuery = setTerm('price')(nextJsonQuery)(undefined);

          break;
        }

        case EOfferType.TradeArea:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.TradeArea);
          break;
        case EOfferType.Warehouse:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.Warehouse);
          break;
        case EOfferType.FreeAppointmentObject:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.FreeAppointmentObject);
          break;
        case EOfferType.PublicCatering:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.PublicCatering);
          break;
        case EOfferType.Manufacture:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.Manufacture);
          break;
        case EOfferType.AutoService:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.AutoService);
          break;
        case EOfferType.Business:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.Business);
          break;
        case EOfferType.Building:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.Building);
          break;
        case EOfferType.DomesticServices:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = toggleOfficeType(nextJsonQuery)(EOfficeType.DomesticServices);
          break;
        case EOfferType.CommercialLand:
          nextJsonQuery = setOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerm('office_type')(nextJsonQuery)(undefined);
          if (dealType & EDealType.Rent) {
            nextJsonQuery = setTerms('category')(nextJsonQuery)([ECategoryType.CommercialLandRent]);
          } else if (dealType & EDealType.Sale) {
            nextJsonQuery = setTerms('category')(nextJsonQuery)([ECategoryType.CommercialLandSale]);
          }
          break;
        default:
      }
    }

    return nextJsonQuery;
  };
}

function selectSuburbanObjectType(jsonQuery: IJsonQuery) {
  return (objectType: EObjectType): IJsonQuery => {
    if (jsonQuery.object_type) {
      if (!jsonQuery.object_type.value.includes(objectType)) {
        return setTerms('object_type')(jsonQuery)(jsonQuery.object_type.value.concat(objectType));
      } else {
        return setTerms('object_type')(jsonQuery)(jsonQuery.object_type.value.filter(type => type !== objectType));
      }
    } else {
      return setTerms('object_type')(jsonQuery)([objectType]);
    }
  };
}

function selectRoomType(jsonQuery: IJsonQuery) {
  return (roomType: ERoomType): IJsonQuery => {
    if (jsonQuery.room) {
      if (!jsonQuery.room.value.includes(roomType)) {
        return setRoomTypes(jsonQuery)(jsonQuery.room.value.concat(roomType));
      } else {
        return setRoomTypes(jsonQuery)(jsonQuery.room.value.filter(type => type !== roomType));
      }
    } else {
      return setRoomTypes(jsonQuery)([roomType]);
    }
  };
}

function toggleOfficeType(jsonQuery: IJsonQuery) {
  return (officeType: EOfficeType): IJsonQuery => {
    if (jsonQuery.office_type) {
      if (!jsonQuery.office_type.value.includes(officeType)) {
        return setTerms('office_type')(jsonQuery)(jsonQuery.office_type.value.concat(officeType));
      }

      return setTerms('office_type')(jsonQuery)(jsonQuery.office_type.value.filter(type => type !== officeType));
    }

    return setTerms('office_type')(jsonQuery)([officeType]);
  };
}

function setOfferType(jsonQuery: IJsonQuery) {
  return (offerType: 'flat' | 'commercial' | 'suburban' | 'newobject'): IJsonQuery => {
    const currentType = jsonQuery._type;

    const nextType: IJsonQueryType = currentType.replace(
      /flat|commercial|suburban|newobject/,
      offerType,
    ) as IJsonQueryType;

    return setRootType(nextType)(jsonQuery);
  };
}
